import { createContext, useContext } from "react";


import {
    Box,
    Button,
    IconButton,
    Stack,
    Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import MuiChatHistoryBar from "../../components/ChatHistory/MuiChatHistoryBar";
import { useAppStateContext } from "../../state/AppContextProvider";
import { Outlet } from "react-router-dom";
import { MuiSidebar } from "./MuiSidebar";
import RegularIcon7 from "/src/assets/icons-regular-7.svg";
import RegularIcon8 from "/src/assets/icons-regular-8.svg";
import HeaderIcon from "/src/assets/FLWM-FULL LOGO DARK.svg";
import HeaderIconDark from "/src/assets/FLWM-FULL LOGO LIGHT.svg";
import Footer from "../../components/common/Footer";
import AmazonLogo from "/src/assets/amazon-logo.png";
import { AccessContext } from "../../state/AccessProvider";
import { OrchestrationType } from "../../common/models";

// Sample data for suggestion cards
const suggestionCards = [
    {
        id: 1,
        text: "What layout works best for a small studio apartment?",
    },
    {
        id: 2,
        text: "How much material is needed for a 12x15 living room?",
    },
    {
        id: 3,
        text: "What color scheme fits a modern industrial kitchen?",
    },
    {
        id: 4,
        text: "What's a cozy balcony concept for a two-story house?",
    },
];

export const SidebarInsertContext = createContext<{
    sidebarInsertValue: string | null;
    setSidebarInsertValue: (v: string | null) => void;
}>({
    sidebarInsertValue: null,
    setSidebarInsertValue: () => { },
});

export const FooterButtonContext = createContext<{
    footerButtonValue: string | null;
    setFooterButtonValue: (v: string | null) => void;
}>({
    footerButtonValue: null,
    setFooterButtonValue: () => { },
});

export const MuiLayout = () => {
    //Component properties and event handlers
    const appStateContext = useAppStateContext();
    const profilePhoto = appStateContext.profilePhoto;
    const { isPhone, isChromeExtension } = appStateContext.state;
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [sidebarInsertValue, setSidebarInsertValue] = useState<string | null>(null);
    const [footerButtonValue, setFooterButtonValue] = useState<string | null>(null);
    const showWelcome = appStateContext.state.currentConversation.messages.length === 0;
    const accessContext = useContext(AccessContext);

    // Determine if user is a Salesforce user
    const isSalesforceUser = accessContext.extraLoginInfo?.type === "salesforceUser";

    // Add this effect to set userSignedUp if not present
    useEffect(() => {
        if (localStorage.getItem("userSignedUp") !== "true") {
            localStorage.setItem("userSignedUp", "true");
        }
    }, []);

    const handleHistoryClick = () => {
        appStateContext?.dispatch({ type: 'TOGGLE_CHAT_HISTORY' })
    }

    const handleNewChatClick = async() =>{

        // Specify not to ladd continue negotiation option
        appStateContext.setShouldAddContinueOption(false);

        await newChat(OrchestrationType.StartNegotiation);

    }


    const newChat = async (orchestrationType: OrchestrationType) => {
        // First, completely clear the current conversation
        appStateContext.dispatch({ type: "CLEAR_CURRENT_CHAT" });

        //reset max step to 0
        appStateContext.setMaxStep(0);
        
        // Clear active document
        appStateContext.dispatch({
            type: 'SET_ACTIVE_DOCUMENT',
            payload: null
        });

        // Set orchestration type to  provided
        appStateContext.setCurrentOrchestrationType(orchestrationType);
        
        // Wait a tick to ensure state updates are processed
        await new Promise(resolve => setTimeout(resolve, 0));
        
        // Don't immediately reload chat history to avoid restoring attachments
    };
    const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleSidebarInsert = async (text: string, orchestrationType: OrchestrationType) => {
        // Ensure we start a fresh conversation when inserting from the sidebar
        await newChat(orchestrationType);
        setSidebarInsertValue(text);
    };

    return (
        <>
            <Box
                sx={{
                    position: "relative",
                    height: "100vh",
                    bgcolor: "background.default",
                    display: "flex",
                    flexDirection: "column",
                }}
                data-chat-bot-theme-mode="light-mode"
            >
                {/* Header - Full Width */}
                <Box
                    component="header"
                    sx={{
                        position: "relative",
                        width: "100%",
                        bgcolor: "background.paper",
                        borderBottom: 1,
                        borderColor: "divider",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "flex-start",
                        px: isPhone ? 2 : 0,
                        py: 0,
                        ...(isPhone ? {} : { height: "var(--header-height)" }),
                        zIndex: 1100,
                    }}
                >
                        {/* First row: logo left, icons right */}
                        <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                            sx={{
                                width: "100%",
                                height: isPhone ? "var(--header-height-mobile)" : "var(--header-height)",
                                justifyContent: "space-between",
                            }}
                        >
                            <Box
                                component="img"
                                src={appStateContext.state.themeMode === 'light' ? HeaderIcon : HeaderIconDark}
                                alt="NeuAI icon"
                                sx={{
                                    width: isPhone ? 90 : '15%',
                                    maxWidth: isPhone ? '90px' : '245px',
                                    pl: isPhone ? 0 : '50px',
                                }}
                            />
                            <Stack direction="row" spacing={1} alignItems="center">
                                {!isSalesforceUser && (
                                    <>
                                        <IconButton
                                            sx={{
                                                bgcolor: "#e50c20",
                                                borderRadius: 3,
                                                width: { xs: "34px", sm: "40px" }, // shrink by ~5% for mobile
                                                height: { xs: "34px", sm: "40px" }, // shrink by ~5% for mobile
                                                "&:hover": { bgcolor: "#b8091a" },
                                            }}
                                            onClick={handleNewChatClick}
                                        >
                                            <Box
                                                component="img"
                                                src={RegularIcon7}
                                                alt="Action"
                                                width={isPhone ? 18 : 24}
                                                height={isPhone ? 18 : 24}
                                            />
                                        </IconButton>
                                        <IconButton onClick={handleHistoryClick}
                                            sx={{
                                                width: isPhone ? 32 : 40,
                                                height: isPhone ? 32 : 40,
                                            }}
                                        >
                                            <Box
                                                component="img"
                                                src={RegularIcon8}
                                                alt="Menu"
                                                width={isPhone ? 18 : 24}
                                                height={isPhone ? 18 : 24}
                                            />
                                        </IconButton>
                                    </>
                                )}
                            </Stack>
                        </Stack>

                        

                        {/* Desktop: all buttons in one row as before */}

                    </Box>
                    {/* Second row: Book and Audible buttons, only on mobile */}
                        {showWelcome && (
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ width: "100%", justifyContent: "center", mt: isPhone ? 0 : -1, zIndex: 1200, position: 'relative' }}>
                                {/* Book Button */}
                                <Button
                                    variant="contained"
                                    sx={{
                                        background: "linear-gradient(90deg, #e50c20 0%, #b8091a 100%)",
                                        color: "#fff",
                                        fontWeight: 700,
                                        borderRadius: 2,
                                        px: 1,
                                        py: 0.5,
                                        minWidth: 110,
                                        height: 28,
                                        fontSize: "0.85rem",
                                        position: "relative",
                                        boxShadow: "4px 2px 4px 0 rgba(229, 12, 32, 0.4)",
                                        transition: "background 0.3s, box-shadow 0.5s",
                                        "&:hover": {
                                            background: "linear-gradient(90deg, #b8091a 0%, #e50c20 100%)",
                                            boxShadow: "-4px 2px 4px 0 rgba(229, 12, 32, 0.4)",
                                        },
                                        overflow: "visible",
                                    }}
                                    href="https://www.amazon.com/Fight-Less-Win-More-Negotiators/dp/1684016525/ref=sr_1_1?crid=1KP46D392REXA&dib=eyJ2IjoiMSJ9.2xsvZa3BVOtnl4ceyrBHCPi9C4o9ovSRRy7a4IiS13vGFCJcJrf6hvn9TzY8L0DuABcGnUeFfAVR0E_RdzWTMA553YJeFcrMWHYJstghxYst1vWcNOyAa9ajtVDKDc4u9QaQmpPvc_WeIlsrh8ZgdA.7QLtQTiIgmeL7yQquL9MWkE5yBNGaZ91DwVUswqqPKY&dib_tag=se&keywords=fight+less+win+more&qid=1760474710&sprefix=fight+less+win+more%2Caps%2C456&sr=8-1"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    startIcon={null}
                                >
                                    Fight Less, Win More.
                                    <Box
                                        component="img"
                                        src={AmazonLogo}
                                        alt="Amazon"
                                        sx={{
                                            width: 22,
                                            height: 22,
                                            position: "absolute",
                                            left: -16,
                                            bottom: 0,
                                            zIndex: 2,
                                            bgcolor: "transparent",
                                            pointerEvents: "none",
                                            borderRadius: "11px",
                                        }}
                                    />
                                </Button>
                                {/* Audible Button */}
                                <IconButton
                                    sx={{
                                        background: "linear-gradient(90deg, #e50c20 0%, #b8091a 100%)",
                                        color: "#fff",
                                        borderRadius: 2,
                                        width: 85,
                                        height: 28,
                                        ml: 1,
                                        display: "flex",
                                        flexDirection: "row",
                                        justifyContent: "center",
                                        alignItems: "center",
                                        boxShadow: "4px 2px 4px 0 rgba(229, 12, 32, 0.4)",
                                        transition: "background 0.3s, box-shadow 0.5s",
                                        "&:hover": {
                                            background: "linear-gradient(90deg, #b8091a 0%, #e50c20 100%)",
                                            boxShadow: "-4px 2px 4px 0 rgba(229, 12, 32, 0.4)",
                                        },
                                        p: 0,
                                    }}
                                    href="https://www.audible.com/"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    <Box
                                        component="img"
                                        src="https://img.icons8.com/ios-filled/50/000000/headphones.png"
                                        alt="Audible"
                                        sx={{
                                            width: 14,
                                            height: 14,
                                            ml: 1,
                                            mr: 0.5,
                                        }}
                                    />
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            color: "#fff",
                                            fontWeight: 700,
                                            fontSize: "0.85rem",
                                            lineHeight: 1,
                                            ml: 0.5,
                                            pr: 1,
                                            textTransform: "none",
                                        }}
                                    >
                                        Audible
                                    </Typography>
                                </IconButton>
                            </Stack>
                        )}

                {/* Middle Section: Sidebar + Main Content */}
                <Box sx={{ 
                    display: "flex", 
                    flex: 1, 
                    position: "relative",
                    overflow: "hidden"
                }}>
                    {/* Left Sidebar - Hide in Chrome Extension */}
                    {!isChromeExtension && (
                        <MuiSidebar
                            profilePhoto={profilePhoto}
                            handleMenu={handleMenu}
                            anchorEl={anchorEl}
                            handleClose={handleClose}
                            onSidebarInsert={handleSidebarInsert}
                        />
                    )}

                    {/* Main Content */}
                    <Stack
                        sx={{
                            flex: 1,
                            position: "relative",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            overflow: 'auto',
                            height: '100%',
                            width: '100%',
                        }}
                    >
                        <FooterButtonContext.Provider value={{ footerButtonValue, setFooterButtonValue }}>
                            <SidebarInsertContext.Provider value={{ sidebarInsertValue, setSidebarInsertValue }}>
                                <Outlet />
                            </SidebarInsertContext.Provider>
                        </FooterButtonContext.Provider>
                    </Stack>
                </Box>

                {/* Footer - Full Width */}
                <Box sx={{ width: '100%', zIndex: 1100 }}>
                    <Footer />
                </Box>
            </Box>

            <MuiChatHistoryBar />
        </>
    );
};
